/**
 * Exposure Over Time ECharts Adapter
 *
 * Transforms exposure timeseries data into a stacked area chart.
 */

import type { EChartsCoreOption } from "echarts";
import type {
  EChartAdapterResult,
  DataPoint,
  BaseEChartAdapterProps,
  PlaygroundChartSettings,
} from "./types";

export interface ExposureOverTimeAdapterProps extends BaseEChartAdapterProps {
  chartSettings: PlaygroundChartSettings & {
    exposureDisplayMode?: "weights" | "scaled_weights" | "values";
    categories?: string[];
  };
}

function formatAxisDate(date: Date): string {
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

function formatTooltipDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatValue(
  value: number,
  displayMode: "weights" | "scaled_weights" | "values"
): string {
  if (displayMode === "values") {
    return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  const percent = value;
  const rounded = Math.round(percent * 10) / 10;
  return Math.abs(rounded - Math.round(rounded)) < 0.001
    ? `${Math.round(rounded)}%`
    : `${rounded.toFixed(1)}%`;
}

function extractCategories(
  rawData: DataPoint[],
  configuredCategories?: string[]
): string[] {
  if (configuredCategories && configuredCategories.length > 0) {
    return configuredCategories;
  }
  const categories = new Set<string>();
  rawData.forEach((point) => {
    Object.keys(point).forEach((key) => {
      if (key !== "date") {
        categories.add(key);
      }
    });
  });
  return Array.from(categories);
}

export function buildExposureOverTimeOption(
  props: ExposureOverTimeAdapterProps
): EChartAdapterResult {
  const {
    rawData,
    colorPalette,
    isDarkMode,
    visualSettings,
    chartSettings,
  } = props;

  const displayMode = chartSettings.exposureDisplayMode || "scaled_weights";
  const isPercentMode =
    displayMode === "weights" || displayMode === "scaled_weights";

  if (!rawData || rawData.length === 0) {
    return { option: {}, chartType: "line", hasData: false };
  }

  const categories = extractCategories(rawData, chartSettings.categories);
  if (categories.length === 0) {
    return { option: {}, chartType: "line", hasData: false };
  }

  const hasAnyValue = rawData.some((point) =>
    categories.some((category) => {
      const value = point[category];
      return value !== null && value !== undefined;
    })
  );

  if (!hasAnyValue) {
    return { option: {}, chartType: "line", hasData: false };
  }

  const textColor = isDarkMode ? "#e2e8f0" : "#334155";
  const gridColor = isDarkMode ? "#334155" : "#e2e8f0";
  const dates = rawData.map((point) => formatAxisDate(point.date as Date));
  const legendPosition = chartSettings.legendPosition || "right";

  const series = categories.map((category, index) => ({
    name: category,
    type: "line" as const,
    stack: "total",
    areaStyle: { opacity: 0.9 },
    connectNulls: false,
    showSymbol: false,
    emphasis: { focus: "series" as const },
    itemStyle: {
      color: colorPalette[index % colorPalette.length],
    },
    data: rawData.map((point) => {
      const value = point[category];
      if (value === null || value === undefined) {
        return null;
      }
      return isPercentMode ? (value as number) * 100 : (value as number);
    }),
  }));

  const option: EChartsCoreOption = {
    backgroundColor: "transparent",
    tooltip: visualSettings.showTooltips
      ? {
          trigger: "axis",
          axisPointer: { type: "line" },
          backgroundColor: isDarkMode
            ? "rgba(30, 41, 59, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
          borderColor: isDarkMode ? "#475569" : "#e2e8f0",
          textStyle: { color: textColor },
          formatter: (params: unknown) => {
            const items = Array.isArray(params) ? params : [params];
            if (items.length === 0) return "";
            const axisValue = (items[0] as { axisValue?: string }).axisValue;
            const dataIndex = (items[0] as { dataIndex?: number }).dataIndex ?? 0;
            const date = rawData[dataIndex]?.date as Date | undefined;
            const header = date ? formatTooltipDate(date) : axisValue || "";
            const lines = items
              .filter((item) => {
                const value = (item as { value?: number | null }).value;
                return value !== null && value !== undefined;
              })
              .map((item) => {
                const typed = item as {
                  color?: string;
                  seriesName?: string;
                  value?: number;
                };
                return `<div style="display:flex;align-items:center;gap:8px;">
                  <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${typed.color};"></span>
                  <span>${typed.seriesName}:</span>
                  <span style="font-weight:600;">${formatValue(typed.value || 0, displayMode)}</span>
                </div>`;
              });
            return `<div>${header}</div>${lines.join("")}`;
          },
        }
      : undefined,
    legend: {
      type: "scroll",
      orient: legendPosition === "right" ? "vertical" : "horizontal",
      right: legendPosition === "right" ? 10 : undefined,
      top: legendPosition === "right" ? "middle" : undefined,
      bottom: legendPosition === "bottom" ? 0 : undefined,
      textStyle: { color: textColor },
    },
    // Grid margins are for the plot area only — legend is rendered externally by EChartRenderer.
    grid: {
      left: 50,
      right: 20,
      top: 20,
      bottom: 40,
      containLabel: false,
    },
    xAxis: {
      type: "category",
      data: dates,
      boundaryGap: false,
      axisLabel: {
        color: textColor,
        rotate: dates.length > 8 ? 45 : 0,
      },
      axisLine: { lineStyle: { color: gridColor } },
    },
    yAxis: {
      type: "value",
      min: isPercentMode ? 0 : undefined,
      max: isPercentMode ? 100 : undefined,
      axisLabel: {
        color: textColor,
        formatter: (value: number) =>
          isPercentMode ? `${value}%` : value.toLocaleString(),
      },
      splitLine: {
        show: visualSettings.showGridRows,
        lineStyle: { color: gridColor, type: "dashed" },
      },
    },
    series,
  };

  return {
    option,
    chartType: "line",
    hasData: true,
  };
}
